import { MeshBVH } from 'three-mesh-bvh'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'

import * as THREE from '../three'
import { createNode } from '../createNode'
import { Location } from '../../core/Location'

import { CHUNK_DEPTH, CHUNK_HEIGHT, CHUNK_WIDTH, PIXEL_SIZE, REGION_SIZE } from '../../core/chunks'
import { clamp } from '../../core/utils'

// const USE_REGIONS = true
// const SHOW_BOUNDS = false

export class Chunk {
  constructor(client, data) {
    this.client = client
    this.id = data.id
    this.x = parseInt(data.id.split('_')[0])
    this.z = parseInt(data.id.split('_')[1])
    this.index = data.index
    this.states = data.states
    this.lightSAB = new SharedArrayBuffer(4 * CHUNK_WIDTH * CHUNK_HEIGHT * CHUNK_DEPTH)
    this.light = new Uint8Array(this.lightSAB)
    this.damages = {} // x_y_z -> Damage
    for (const coords in data.damages) {
      this.damages[coords] = new Damage(this, coords, data.damages[coords])
    }

    // this.bbox = new THREE.Box3(
    //   new THREE.Vector3(this.x * CHUNK_WIDTH - 0.5, 0, this.z * CHUNK_DEPTH - 0.5),
    //   new THREE.Vector3(
    //     this.x * CHUNK_WIDTH - 0.5 + CHUNK_WIDTH,
    //     CHUNK_HEIGHT,
    //     this.z * CHUNK_DEPTH + CHUNK_DEPTH - 0.5
    //   )
    // )
    // if (SHOW_BOUNDS) {
    //   const bb = this.bbox.clone()
    //   bb.min.x += PIXEL_SIZE * 0.1
    //   bb.min.z -= PIXEL_SIZE * 0.1
    //   bb.max.x -= PIXEL_SIZE * 0.1
    //   bb.max.z += PIXEL_SIZE * 0.1
    //   this.bboxMesh = new THREE.Box3Helper(bb, 0xff0000)
    // }

    // this.material = this.client.chunks.atlas.material.base
    // this.customDepthMaterial = this.client.chunks.material.depth

    this.root = createNode('group', {
      position: [this.x * CHUNK_WIDTH, 0, this.z * CHUNK_DEPTH],
    })
    this.root.activate({ client: this.client })

    this.handle = this.client.chunks.terrain.register({
      id: this.id,
      states: this.states,
      index: this.index,
      lightSAB: this.lightSAB,
      onCollider: collider => {
        this.setCollider(collider)
      },
    })
    // this.client.chunks.sendToChunkWorker('addChunk', { data, lightSAB: this.lightSAB })
  }

  getDamageAt(loc) {
    return this.damages[loc.voxelId]?.amount || 0
  }

  setDamageAt(loc, amount) {
    amount = clamp(amount, 0, 1)
    console.log('setDamageAt', loc.voxelId, amount)
    let damage = this.damages[loc.voxelId]
    if (amount) {
      // spawn/update
      if (!damage) {
        damage = new Damage(this, loc.voxelId, amount)
        this.damages[loc.voxelId] = damage
      }
      damage.amount = amount
    } else {
      // destroy
      if (damage) {
        damage.destroy()
        delete this.damages[loc.voxelId]
      }
    }
  }

  setCollider(collider) {
    if (this.body) {
      this.root.remove(this.body)
      this.body = null
    }
    if (collider) {
      // console.time('build collider')
      const arr = collider.pxData
      const buf = PHYSX._malloc(arr.length)
      PHYSX.HEAPU8.set(arr, buf)
      const vector = new PHYSX.Vector_PxU8()
      vector.setFromBuffer(buf, arr.length)
      PHYSX._webidl_free(buf)
      const inputStream = new PHYSX.PxDefaultMemoryInputData(vector, vector.size())
      this.pxTriangleMesh = this.client.physics.physics.createTriangleMesh(inputStream)
      PHYSX.destroy(inputStream)
      PHYSX.destroy(vector)
      // console.timeEnd('build collider')

      // console.time('mount collider')
      const coll = createNode('collider', {
        type: 'geometry',
        geometry: this.pxTriangleMesh,
      })
      this.body = createNode('rigidbody')
      this.body.type = 'static'
      this.root.add(this.body)
      this.body.add(coll)
      // console.timeEnd('mount collider')
    }
  }

  // async build({ geometry, collider }) {
  //   if (this.dead) return

  //   // const camera = this.client.graphics.camera
  //   // const frustumProjMat = new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
  //   // const frustum = new THREE.Frustum().setFromProjectionMatrix(frustumProjMat)
  //   // console.log(frustum.intersectsBox(this.bbox))

  //   // console.time('--- build')

  //   // console.log('mt build', this.id, this.client.frame)
  //   // console.log('geo', geometry)

  //   this.unbuild()

  //   if (SHOW_BOUNDS) {
  //     this.client.graphics.scene.add(this.bboxMesh)
  //   }

  //   this.body = createNode('rigidbody')
  //   this.body.type = 'static'
  //   this.root.add(this.body)

  //   if (geometry) {
  //     if (USE_REGIONS) {
  //       this.client.regions.upsertChunkGeometry(this.id, this.x, this.z, geometry)
  //     } else {
  //       this.geometry = new THREE.BufferGeometry()
  //       // add attributes (much faster than below)
  //       this.geometry.setAttribute('position', new THREE.BufferAttribute(geometry.position, 3))
  //       // this.geometry.setAttribute('normal', new THREE.BufferAttribute(geometry.normal, 3))
  //       this.geometry.setAttribute('tileId', new THREE.BufferAttribute(geometry.tileId, 1))
  //       this.geometry.setAttribute('uv', new THREE.BufferAttribute(geometry.uv, 2))
  //       this.geometry.setAttribute('light', new THREE.BufferAttribute(geometry.light, 4))
  //       this.geometry.setAttribute('ao', new THREE.BufferAttribute(geometry.ao, 1))
  //       this.geometry.setAttribute('dim', new THREE.BufferAttribute(geometry.dim, 1))
  //       this.geometry.setIndex(new THREE.BufferAttribute(geometry.index, 1))
  //       // add attributes (this is like 2-5x slower than above)
  //       // this.geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3))
  //       // this.geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3))
  //       // this.geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  //       // this.geometry.setIndex(indices)
  //       // this.geometry.boundsTree = MeshBVH.deserialize(geometry.bvh, this.geometry)

  //       const mesh = createNode('mesh')
  //       mesh.type = 'geometry'
  //       mesh.geometry = this.geometry
  //       mesh.material = this.material
  //       mesh.customDepthMaterial = this.customDepthMaterial
  //       this.body.add(mesh)
  //     }
  //   }

  //   if (collider) {
  //     // console.time('build collider')
  //     const arr = collider.pxData
  //     const buf = PHYSX._malloc(arr.length)
  //     PHYSX.HEAPU8.set(arr, buf)
  //     const vector = new PHYSX.Vector_PxU8()
  //     vector.setFromBuffer(buf, arr.length)
  //     PHYSX._webidl_free(buf)
  //     const inputStream = new PHYSX.PxDefaultMemoryInputData(vector, vector.size())
  //     this.pxTriangleMesh = this.client.physics.physics.createTriangleMesh(inputStream)
  //     PHYSX.destroy(inputStream)
  //     PHYSX.destroy(vector)
  //     // console.timeEnd('build collider')

  //     // console.time('mount collider')
  //     const coll = createNode('collider', {
  //       type: 'geometry',
  //       geometry: this.pxTriangleMesh,
  //     })
  //     this.body.add(coll)
  //     // console.timeEnd('mount collider')
  //   }

  //   this.built = true
  //   this.onBuild?.()

  //   // console.timeEnd('--- build')
  // }

  // unbuild() {
  //   if (this.dead) return
  //   // if (this.body) {
  //   //   this.root.remove(this.body)
  //   //   this.body = null
  //   // }
  //   // if (USE_REGIONS) {
  //   //   if (this.built) {
  //   //     this.client.regions.removeChunkGeometry(this.id, this.x, this.z)
  //   //   }
  //   // } else {
  //   //   if (this.geometry) {
  //   //     this.geometry.dispose()
  //   //     this.geometry = null
  //   //   }
  //   // }
  //   // if (this.pxTriangleMesh) {
  //   //   this.pxTriangleMesh.release()
  //   //   this.pxTriangleMesh = null
  //   // }
  //   // if (SHOW_BOUNDS && this.bboxMesh.parent) {
  //   //   this.client.graphics.scene.remove(this.bboxMesh)
  //   // }
  //   this.built = false
  // }

  getLight(idx) {
    return [
      Atomics.load(this.light, idx * 4 + 0),
      Atomics.load(this.light, idx * 4 + 1),
      Atomics.load(this.light, idx * 4 + 2),
      Atomics.load(this.light, idx * 4 + 3),
    ]
    return
    // const sky = light & 0x0f // lower 4 bits
    // const block = light >> 4 // upper 4 bits
    // return Math.max(sky, block)
  }

  getBlockAtIdx(idx) {
    const state = this.getStateAtIdx(idx)
    return this.client.registry.getBlockByState(state)
  }

  setBlockAtIdx(idx, block) {
    const state = block.state
    this.setStateAtIdx(idx, state)
  }

  getStateAtIdx(idx) {
    return this.states[this.index[idx]]
  }

  setStateAtIdx(idx, state) {
    let stateIdx = this.states.indexOf(state)
    if (stateIdx === -1) {
      this.states.push(state)
      stateIdx = this.states.length - 1
    }
    this.index[idx] = stateIdx
    // this.edited = true
    // this.pending = 'upsert'
    // this.client.chunks.builder.edit({
    //   id: this.id,
    //   idx,
    //   stateIdx,
    //   state,
    // })
  }

  destroy() {
    this.built = false
    // this.unbuild()
    this.dead = true
    this.handle.destroy()
    this.handle = null
    // this.client.chunks.sendToChunkWorker('removeChunk', { id: this.id })
  }
}

class Damage {
  constructor(chunk, coords, amount) {
    this.chunk = chunk
    this.location = new Location()
    this.location.voxel.set(...coords.split('_').map(s => parseInt(s)))
    this._amount = amount // 0-1

    const block = chunk.getBlockAtIdx(this.location.voxelIdx)

    const geometry = block.damageModel.geometry
    const material = chunk.client.chunks.atlas.material.getDamageMaterial()
    material.uniforms.uDamage.value = amount
    this.mesh = new THREE.Mesh(geometry, material)
    this.mesh.position.copy(this.location.world)
    this.chunk.client.graphics.scene.add(this.mesh)
  }

  get amount() {
    return this._amount
  }

  set amount(value) {
    this._amount = value
    this.mesh.material.uniforms.uDamage.value = value
  }

  destroy() {
    this.chunk.client.graphics.scene.remove(this.mesh)
    // this.mesh.geometry.dispose()
    this.mesh.material.dispose()
  }
}
